import { buildManifestIndex } from '../content/portfolioManifest.js';

const LEVEL_LABEL = {
  high: 'Alto',
  mid: 'Médio',
  support: 'Apoio',
};

/**
 * Tabuleiro RPG minimalista — quests / atributos / loot.
 */
export class RpgBoard {
  /**
   * @param {HTMLElement} root
   */
  constructor(root) {
    this.root = root;
    this.index = buildManifestIndex();
    this.root.hidden = true;
    this.root.setAttribute('aria-live', 'polite');
  }

  /**
   * Fallback de loading (o fluxo principal usa DungeonRitual).
   * Mantido para reuso pontual / degraded path.
   */
  showSkeleton() {
    this.root.hidden = false;
    this.root.dataset.state = 'loading';
    this.root.innerHTML = `
      <div class="board board--skeleton" aria-busy="true">
        <div class="board__map-gen" aria-hidden="true">
          <span class="board__die-spin"></span>
          <span class="board__die-spin board__die-spin--delay"></span>
          <span class="board__die-spin board__die-spin--delay2"></span>
        </div>
        <p class="board__loading-label">Gerando o mapa da mesa…</p>
        <p class="board__loading-sub">Rolando correlação semântica com o portfólio</p>
        <div class="board__skel-grid">
          <div class="board__skel-block"></div>
          <div class="board__skel-block"></div>
          <div class="board__skel-block"></div>
        </div>
      </div>
    `;
  }

  /**
   * @param {object} board
   * @param {{ isCached?: boolean, source?: string|null, similarity?: number|null, query?: string }} meta
   */
  render(board, meta = {}) {
    this.root.hidden = false;
    this.root.dataset.state = 'ready';

    const cacheNote = meta.isCached
      ? meta.source === 'cache_semantic'
        ? `Resposta em cache semântico${
            typeof meta.similarity === 'number'
              ? ` (${Math.round(meta.similarity * 100)}%)`
              : ''
          }`
        : 'Resposta em cache (consulta idêntica)'
      : 'Gerado agora via Gemini';

    const quests = (board.quests ?? [])
      .map((q, i) => this._questCard(q, i))
      .join('');
    const attrs = (board.attributes ?? [])
      .map((a, i) => this._attrCard(a, i))
      .join('');
    const loot = (board.loot ?? [])
      .map((l, i) => this._lootCard(l, i))
      .join('');

    this.root.innerHTML = `
      <article class="board">
        <header class="board__header">
          <p class="board__kicker">Mesa de evidências</p>
          <h2 class="board__title">${escapeHtml(board.board_title || 'Tabuleiro')}</h2>
          <p class="board__narrative">${escapeHtml(board.narrative || '')}</p>
          <div class="board__meta">
            <span class="board__chip">${escapeHtml(cacheNote)}</span>
            ${
              meta.query
                ? `<span class="board__chip board__chip--muted">“${escapeHtml(meta.query)}”</span>`
                : ''
            }
          </div>
        </header>

        <section class="board__zone board__zone--quests" aria-labelledby="zone-quests">
          <h3 id="zone-quests" class="board__zone-title">
            <span class="board__zone-mark" aria-hidden="true">Q</span>
            Quests concluídas
          </h3>
          <p class="board__zone-sub">Projetos e casos alinhados à sua intenção</p>
          <div class="board__cards">${quests || emptyZone('Nenhuma quest correlacionada')}</div>
        </section>

        <section class="board__zone board__zone--attrs" aria-labelledby="zone-attrs">
          <h3 id="zone-attrs" class="board__zone-title">
            <span class="board__zone-mark" aria-hidden="true">A</span>
            Atributos do herói
          </h3>
          <p class="board__zone-sub">Tecnologias e competências em jogo</p>
          <div class="board__cards board__cards--attrs">${attrs || emptyZone('Sem atributos neste recorte')}</div>
        </section>

        <section class="board__zone board__zone--loot" aria-labelledby="zone-loot">
          <h3 id="zone-loot" class="board__zone-title">
            <span class="board__zone-mark" aria-hidden="true">L</span>
            Recompensas / Loot
          </h3>
          <p class="board__zone-sub">Resultados mensuráveis extraídos dos casos</p>
          <div class="board__cards board__cards--loot">${loot || emptyZone('Sem loot explícito neste recorte')}</div>
        </section>

        <footer class="board__footer">
          <button type="button" class="board__reset" id="board-reset">Nova busca</button>
          <a class="board__link" href="./perfil.html" target="_blank" rel="noopener">Ver perfil completo</a>
        </footer>
      </article>
    `;

    this.root.querySelector('#board-reset')?.addEventListener('click', () => {
      this.root.dispatchEvent(new CustomEvent('board:reset', { bubbles: true }));
    });

    requestAnimationFrame(() => {
      this.root.querySelector('.board')?.classList.add('is-revealed');
    });
  }

  showError(message) {
    this.root.hidden = false;
    this.root.dataset.state = 'error';
    this.root.innerHTML = `
      <div class="board board--error">
        <h2 class="board__title">Falha ao projetar a mesa</h2>
        <p class="board__narrative">${escapeHtml(message)}</p>
        <button type="button" class="board__reset" id="board-reset">Tentar de novo</button>
      </div>
    `;
    this.root.querySelector('#board-reset')?.addEventListener('click', () => {
      this.root.dispatchEvent(new CustomEvent('board:reset', { bubbles: true }));
    });
  }

  hide() {
    this.root.hidden = true;
    this.root.innerHTML = '';
    this.root.dataset.state = '';
  }

  _questCard(q, i) {
    const full = this.index.get(q.id);
    // Preferir texto completo do manifesto quando o payload veio truncado/cache antigo
    const title = pickFuller(full?.title, q.title) || q.id;
    const summary = pickFuller(full?.text, q.summary);
    return `
      <article class="board-card board-card--quest" style="--i:${i}">
        <p class="board-card__id">${escapeHtml(q.id)}</p>
        <h4 class="board-card__title">${escapeHtml(title)}</h4>
        <p class="board-card__body">${escapeHtml(summary)}</p>
        ${
          q.relevance
            ? `<p class="board-card__relevance">${escapeHtml(q.relevance)}</p>`
            : ''
        }
      </article>
    `;
  }

  _attrCard(a, i) {
    const full = this.index.get(a.id);
    const name = pickFuller(full?.title, a.name) || a.id;
    const body = pickFuller(full?.text, a.why);
    return `
      <article class="board-card board-card--attr" style="--i:${i}" data-level="${escapeHtml(a.level || 'mid')}">
        <div class="board-card__attr-top">
          <p class="board-card__id">${escapeHtml(a.id)}</p>
          <span class="board-card__level">${LEVEL_LABEL[a.level] || a.level || '—'}</span>
        </div>
        <h4 class="board-card__title">${escapeHtml(name)}</h4>
        <p class="board-card__body">${escapeHtml(body)}</p>
      </article>
    `;
  }

  _lootCard(l, i) {
    const full = l.source_id ? this.index.get(l.source_id) : null;
    // Se o loot veio como snippet no meio da frase, preferir texto completo da fonte
    const value = looksTruncated(l.value)
      ? pickFuller(full?.text, l.value)
      : l.value || '';
    return `
      <article class="board-card board-card--loot" style="--i:${i}">
        <p class="board-card__loot-label">${escapeHtml(l.label || 'Resultado')}</p>
        <p class="board-card__loot-value">${escapeHtml(value)}</p>
        ${
          l.source_id
            ? `<p class="board-card__id">${escapeHtml(l.source_id)}</p>`
            : ''
        }
      </article>
    `;
  }
}

function emptyZone(msg) {
  return `<p class="board__empty">${escapeHtml(msg)}</p>`;
}

/** Escolhe a versão mais completa entre manifesto e payload da API. */
function pickFuller(preferred, fallback) {
  const a = String(preferred ?? '').trim();
  const b = String(fallback ?? '').trim();
  if (!a) return b;
  if (!b) return a;
  if (a.length >= b.length) return a;
  // payload maior só se claramente mais completo (não snippet)
  if (!looksTruncated(b) && b.length > a.length) return b;
  return a;
}

/** Heurística: começa minúsculo / corta no meio / termina sem pontuação útil. */
function looksTruncated(value) {
  const t = String(value ?? '').trim();
  if (!t) return false;
  if (/^[a-zà-ü]/.test(t)) return true;
  if (/[a-zà-ü0-9]$/i.test(t) && !/[.!?…)]$/.test(t) && t.length < 220) {
    // frases curtas sem ponto final e que parecem cortadas (ex.: "…elevou 70% o")
    if (/\b(o|a|de|com|para|e|em|do|da)\s*$/i.test(t)) return true;
  }
  return false;
}

function escapeHtml(value) {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}
